import { TriggerProps } from '@atlaskit/popup';
import { useContextualMenu } from '@databases/contextual-menu';
import { useFlaggingCallback } from '@databases/flags';
import { isModifierKeyPressed, useComponent } from '@databases/react';
import { defaultStoreUtils } from '@databases/redux';
import {
  selectEntryMulti,
  selectEntrySingle,
} from '@databases/redux/cellSelections/actions';
import { selectIsEntireEntrySelected } from '@databases/redux/cellSelections/selectors';
import composeRefs from '@seznam/compose-react-refs';
import React, { ForwardedRef, forwardRef } from 'react';

import { isApple } from '../../../utils';
import { useScope } from '../../Scope';
import SelectEntryMenu from '../contextual-menu/SelectEntryMenu';

import EntryCard, { EntryCardProps, EntryCardRefs } from './EntryCard';

type EntryCardWithMenuProps = Omit<
  EntryCardProps,
  'handleSelectEntry' | 'handleOnContextMenu'
> & { currentViewId: string | undefined };

const EntryCardWithMenu = forwardRef<
  EntryCardRefs<
    ForwardedRef<HTMLElement | null> | undefined,
    ForwardedRef<HTMLDivElement | null>
  >,
  EntryCardWithMenuProps
>(function EntryCardWithMenu(
  { contentId, viewSettings, entryId, isReadonly, currentViewId, ...entryCardProps },
  ref
) {
  const store = defaultStoreUtils.useStore();
  const dispatch = defaultStoreUtils.useDispatch();
  const scope = useScope();

  const handleSelectEntry = useFlaggingCallback(
    (e: React.MouseEvent) => {
      const isMultiSelectOn = isModifierKeyPressed(e, [
        isApple ? 'metaKey' : 'ctrlKey',
        'shiftKey',
      ]);
      const isRangeSelectOn = isModifierKeyPressed(e, ['shiftKey']);
      if (isRangeSelectOn) {
        e.preventDefault();
        dispatch(
          selectEntryMulti({
            contentId,
            entryId,
            isRangeSelect: !isMultiSelectOn,
          })
        );
      } else {
        if (!isModifierKeyPressed(e)) {
          dispatch(
            selectEntrySingle({
              contentId,
              entryId,
            })
          );
        }
      }
    },
    [dispatch, contentId, entryId]
  );

  const { toggleOpenMenu } = useContextualMenu({
    type: 'entry',
    entryId,
  });

  const trigger = useComponent(
    ({ ref: triggerRef, ...triggerProps }: TriggerProps) => (
      <EntryCard
        contentId={contentId}
        entryId={entryId}
        viewSettings={viewSettings}
        handleSelectEntry={handleSelectEntry}
        isReadonly={isReadonly}
        handleOnContextMenu={(e: React.MouseEvent) => {
          const isSelected = selectIsEntireEntrySelected(store.getState(), {
            entryId,
          });
          if (!isSelected) {
            handleSelectEntry(e);
          }
          if (!isReadonly) {
            toggleOpenMenu();
            e.preventDefault();
          }
        }}
        {...entryCardProps}
        {...triggerProps}
        ref={{
          current: {
            entryCardRef: composeRefs(triggerRef, ref),
          },
        }}
      />
    ),
    [
      contentId,
      entryCardProps,
      entryId,
      handleSelectEntry,
      isReadonly,
      ref,
      store,
      toggleOpenMenu,
      viewSettings,
    ]
  );

  return (
    <SelectEntryMenu
      scope={scope}
      trigger={trigger}
      contentId={contentId}
      entryId={entryId}
      canEditContent={!isReadonly}
      handleSelectEntry={handleSelectEntry}
      currentViewId={currentViewId}
    />
  );
});

export default EntryCardWithMenu;
